import { FormEvent } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CategoryResult } from "@/types/supabase";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Globe,
  MapPinned,
  MessageCircle,
  Search,
  Plus,
  UserPlus,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getGroupSelectionState } from "@/utils/areaSelection";
import {
  areaPillClassName,
  areaPillRemoveClassName,
  categoryPillClassName,
  categoryPillRemoveClassName,
} from "@/components/shared/selectionPillStyles";

interface FormData {
  name: string;
  categories: string[];
  description: string;
  websiteUrl: string;
  suburbs: string[];
  imageUrl: string;
  isFeatured: boolean;
  isVerified: boolean;
  // Contact fields for unassigned listings
  whatsappNumber: string;
  // User assignment
  assignedUserId: string | null;
}

interface AdminEditListingFormProps {
  formData: FormData;
  categories: CategoryResult[];
  areasGrouped: Record<string, string[]>;
  users: Array<{ id: string; first_name: string; last_name: string; email: string; whatsapp_number: string | null }>;
  isDisabled: boolean;
  isUnassigned?: boolean;
  onSubmit: (e: FormEvent) => void;
  onFieldChange: (field: string, value: unknown) => void;
  onToggle: (field: string) => void;
  onCategorySelect: (categoryName: string) => void;
  onCategoryRemove: (category: string) => void;
  onSuburbAdd: (suburb: string) => void;
  onSuburbRemove: (suburb: string) => void;
}

const AdminEditListingForm = ({
  formData,
  categories,
  areasGrouped,
  users,
  isDisabled,
  isUnassigned = false,
  onSubmit,
  onFieldChange,
  onToggle,
  onCategorySelect,
  onCategoryRemove,
  onSuburbAdd,
  onSuburbRemove
}: AdminEditListingFormProps) => {
  const handleBulkSuburbSelect = (suburbs: string[]) => {
    suburbs.forEach((suburb) => {
      if (!formData.suburbs.includes(suburb)) {
        onSuburbAdd(suburb);
      }
    });
  };

  const handleBulkSuburbRemove = (suburbs: string[]) => {
    suburbs.forEach((suburb) => {
      if (formData.suburbs.includes(suburb)) {
        onSuburbRemove(suburb);
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <form onSubmit={onSubmit}>
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Service Name / Provider Name
          </label>
          <input
            type="text"
            id="name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp focus:border-transparent"
            value={formData.name}
            onChange={(e) => onFieldChange("name", e.target.value)}
            required
            disabled={isDisabled}
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories
          </label>
          <div className="mb-2">
            <Select onValueChange={onCategorySelect} disabled={isDisabled}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select categories" />
              </SelectTrigger>
              <SelectContent>
                {categories
                  .filter(category => !formData.categories.includes(category.name))
                  .map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.categories.length > 0 ? (
              formData.categories.map((category) => (
                <Badge key={category} variant="secondary" className={categoryPillClassName}>
                  {category}
                  <button
                    type="button"
                    onClick={() => onCategoryRemove(category)}
                    className={`ml-1 ${categoryPillRemoveClassName}`}
                    disabled={isDisabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500">Please select at least one category</p>
            )}
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Suburbs Serviced
          </label>
          <div className="mb-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between" disabled={isDisabled}>
                  <span className="flex items-center gap-2 truncate">
                    <MapPinned className="h-4 w-4 text-muted-foreground" />
                    {formData.suburbs.length > 0 ? `${formData.suburbs.length} area${formData.suburbs.length === 1 ? "" : "s"} selected` : "Search and select areas"}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0 bg-white shadow-lg border z-50" align="start">
                <Command>
                  <CommandInput placeholder="Search territories or areas..." />
                  <CommandList>
                    <CommandEmpty>No matching areas found.</CommandEmpty>
                    {Object.entries(areasGrouped).map(([territory, suburbs]) => {
                      const selectionState = getGroupSelectionState(suburbs, formData.suburbs);

                      return (
                        <CommandGroup key={territory} heading={territory}>
                          <CommandItem
                            value={`all ${territory} ${suburbs.join(" ")}`}
                            onSelect={() => {
                              if (selectionState === "all") {
                                handleBulkSuburbRemove(suburbs);
                              } else {
                                handleBulkSuburbSelect(suburbs);
                              }
                            }}
                            disabled={isDisabled}
                            className="font-medium"
                          >
                            {selectionState === "all" ? (
                              <CheckCircle2 className="mr-2 h-4 w-4 text-whatsapp" />
                            ) : selectionState === "partial" ? (
                              <Check className="mr-2 h-4 w-4 text-whatsapp" />
                            ) : (
                              <Plus className="mr-2 h-4 w-4 text-muted-foreground" />
                            )}
                            <span>
                              {selectionState === "all" ? `Clear ${territory}` : `Select all in ${territory}`}
                            </span>
                          </CommandItem>
                          {suburbs.map((suburb) => {
                            const isSelected = formData.suburbs.includes(suburb);

                            return (
                              <CommandItem
                                key={`${territory}-${suburb}`}
                                value={`${suburb} ${territory}`}
                                onSelect={() => {
                                  if (isSelected) {
                                    onSuburbRemove(suburb);
                                  } else {
                                    onSuburbAdd(suburb);
                                  }
                                }}
                                disabled={isDisabled}
                                className={cn(isSelected && "text-whatsapp font-medium")}
                              >
                                <CheckCircle2 className={cn("mr-2 h-4 w-4", isSelected ? "text-whatsapp opacity-100" : "opacity-0")} />
                                <span>{suburb}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.suburbs.length > 0 ? (
              formData.suburbs.map((suburb) => (
                <Badge key={suburb} variant="secondary" className={areaPillClassName}>
                  {suburb}
                  <button
                    type="button"
                    onClick={() => onSuburbRemove(suburb)}
                    className={`ml-1 ${areaPillRemoveClassName}`}
                    disabled={isDisabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500">No suburbs selected</p>
            )}
          </div>
        </div>
        
        <div className="mb-6">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="description"
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp focus:border-transparent"
            value={formData.description}
            onChange={(e) => onFieldChange("description", e.target.value)}
            required
            disabled={isDisabled}
          />
        </div>

        <div className="mb-6">
          <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
            <Globe className="h-4 w-4 mr-2" /> Website URL (Optional)
          </label>
          <Input
            type="text"
            id="websiteUrl"
            value={formData.websiteUrl || ""}
            onChange={(e) => onFieldChange("websiteUrl", e.target.value)}
            placeholder="https://www.example.com"
            className="w-full"
            disabled={isDisabled}
          />
          <p className="text-sm text-gray-500 mt-1">
            Link to the service provider's website or portfolio.
          </p>
        </div>

        {/* Listing Features Section */}
        <div className="mb-6 bg-gray-50 rounded-lg p-4 border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Listing Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant={formData.isFeatured ? "default" : "outline"}
                className={`w-full ${formData.isFeatured ? "bg-whatsapp hover:bg-whatsapp-dark" : ""}`}
                onClick={() => onToggle("isFeatured")}
                disabled={isDisabled}
              >
                {formData.isFeatured ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Featured
                  </>
                ) : (
                  "Set as Featured"
                )}
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant={formData.isVerified ? "default" : "outline"}
                className={`w-full ${formData.isVerified ? "bg-green-600 hover:bg-green-700" : ""}`}
                onClick={() => onToggle("isVerified")}
                disabled={isDisabled}
              >
                {formData.isVerified ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Verified
                  </>
                ) : (
                  "Mark as Verified"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* User Assignment & Contact Information Section */}
        {isUnassigned && (
          <div className="mb-6 rounded-lg border border-whatsapp/20 bg-whatsapp-light/40 p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Assignment & Contact Information</h3>
            
            {/* User Assignment */}
            <div className="mb-6">
              <label htmlFor="assignedUserId" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <UserPlus className="h-4 w-4 mr-2" /> Assign to User
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                    disabled={isDisabled}
                  >
                    {formData.assignedUserId ? (
                      users.find(user => user.id === formData.assignedUserId)?.first_name + " " + 
                      users.find(user => user.id === formData.assignedUserId)?.last_name + 
                      " (" + users.find(user => user.id === formData.assignedUserId)?.email + ")"
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Search and select user...
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-white shadow-lg border z-50">
                  <Command>
                    <CommandInput placeholder="Search users by name, email, or WhatsApp..." />
                    <CommandList>
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup heading="Users">
                        <CommandItem
                          onSelect={() => onFieldChange("assignedUserId", null)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center">
                            <div className="h-4 w-4 mr-2" />
                            <span className="text-gray-500 italic">Unassigned</span>
                          </div>
                        </CommandItem>
                        {users.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={`${user.first_name} ${user.last_name} ${user.email} ${user.whatsapp_number || ''}`}
                            onSelect={() => onFieldChange("assignedUserId", user.id)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center w-full">
                              {formData.assignedUserId === user.id && (
                                <Check className="h-4 w-4 mr-2 text-whatsapp" />
                              )}
                              <div className={formData.assignedUserId === user.id ? "ml-0" : "ml-6"}>
                                <div className="font-medium">{user.first_name} {user.last_name}</div>
                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                  <span>{user.email}</span>
                                  {user.whatsapp_number && (
                                    <>
                                      <span>•</span>
                                      <span>{user.whatsapp_number}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-sm text-gray-500 mt-1">
                Assign this listing to a registered user. When assigned, contact fields will be cleared and the user will manage their own contact information.
              </p>
            </div>

            {/* Contact Information for Unassigned Listings */}
            <div className="border-t border-whatsapp/20 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp Contact (For Unassigned Listings)
              </h4>
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Number
                </label>
                <Input
                  type="text"
                  id="whatsappNumber"
                  value={formData.whatsappNumber || ""}
                  onChange={(e) => onFieldChange("whatsappNumber", e.target.value)}
                  placeholder="+1234567890"
                  className="w-full"
                  disabled={isDisabled}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                WhatsApp contact information for unassigned listings that haven't been claimed by a provider yet.
              </p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default AdminEditListingForm;
